import {IRegPointer} from "/@lib@/commons/registry.js";
import {IChainEnv} from "/@lib@/wsp/chain.js";
import {Action} from "/@lib@/commons/actions.js";
import {Desk} from "/@lib@/commons/desk.js";
import {ICreateWspCtx, IWspActionCtx} from "/@back@/wsp/actions/wspActions.js";
import {IWspUiEnv, Wsp} from "/@lib@/wsp/wsp.js";
import {AppFrameDeskFeat} from "/@back@/core/appFrame.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {JWspAppDef, JWspDocAppDef} from "/@back@/wsp/plugins/wspsPlg.js";


/**
 * Actions de l'univers chain (niveau entrepot)
 */
export abstract class ChainAction<E extends IRegPointer<IChainEnv>> extends Action<E> {
	isBackendDb?: boolean;
	isBackendFs?: boolean;
	checkCompatKey?: string | null;

	isEnabled(ctx: E): boolean {
		if (this.checkCompatKey && !Desk.checkCompat(this.checkCompatKey)) return false;
		if (!ctx.reg || !ctx.reg.env || !ctx.reg.env.universe) return false;
		return super.isEnabled(ctx);
	}

	isVisible(ctx: E): boolean {
		if (this.isBackendDb && ctx.reg.env.universe.config.backEnd === "fs") return false;
		if (this.isBackendFs && ctx.reg.env.universe.config.backEnd === "odb") return false;
		return super.isVisible(ctx);
	}

	getDescription(ctx: E): string {
		if (this.checkCompatKey && !Desk.checkCompat(this.checkCompatKey)) return "Ce navigateur n'est pas compatible avec cette fonction";
		return super.getDescription(ctx);
	}

	/** Fct mutualisée de l'ouverture d'un wsp après création. */
	protected openWspAfterCreate(ctx: IWspActionCtx & ICreateWspCtx, newWsp: Wsp | null) {
		if (newWsp && ctx.openWspAfterCreate !== false) {
			// Ouverture de l'atelier
			// Remarque : wspDoc et wsp ouvriront l'écran des propriétés du wsp si celui-ci est en erreur => non géré ici
			const reg = ctx.reg;
			if (!reg.getUserData("wspAppPrefered", true) && reg.hasPerm("ui.wspDocApp")) {
				(desk as AppFrameDeskFeat).findAndOpenApp({u: reg.env.universe.getId(), wspDoc: newWsp.code} as JWspDocAppDef);
			} else if (reg.hasPerm("ui.wspApp")) {
				(desk as AppFrameDeskFeat).findAndOpenApp({u: reg.env.universe.getId(), wsp: newWsp.code} as JWspAppDef);
			}
		}
	}
}

/** Création d'un atelier
 *		IRegPointer<IWspUiEnv> permet de créer un atelier en se basant sur les propriétés existantes d'un autre atelier
 * */
export class CreateWspAction extends ChainAction<IRegPointer<IWspUiEnv>> {
	static SINGLETON = new CreateWspAction();

	constructor(id?: string) {
		super(id || "createWsp");
		this.checkCompatKey = "formElt";
		this._description = this._label = "Créer un atelier...";
		this._group = "add";
		this.setIcon("/@skin@/wsp/actions/wspCreate.svg");
		this.requireEnabledPerm("action.chain#create.wsp");
	}

	async execute(ctx: IRegPointer<IWspUiEnv> & ICreateWspCtx, ev?: Event): Promise<Wsp | null> {
		const reg = ctx.reg;
		const {WspCreateProps} = await import("/@back@/wsp/dialogs/wspProps.js");
		const wsp = await POPUP.showDialog<Wsp | null>(new WspCreateProps().initialize({
			reg: reg.env.universe.reg,
		}), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: "Création d'un atelier"}, closeButton: {}},
			resizer: {},
			initWidth: '45em',
			initHeight: '70vh',
			viewPortX: "middle",
			viewPortY: "middle"
		}).onNextClose();
		this.openWspAfterCreate(ctx, wsp);
		return wsp;
	}
}

/** Import d'un atelier */
export class ImportWspAction extends ChainAction<IRegPointer<IChainEnv>> {
	static SINGLETON = new ImportWspAction();

	constructor(id?: string) {
		super(id || "importWsp");
		this.checkCompatKey = "formElt";
		this._label = "Importer un atelier...";
		this._description = "Import d'un atelier à partir d'une archive (.scwsp, .scar)";
		this._group = "add";
		this.setIcon("/@skin@/wsp/actions/wspImport.svg");
		this.requireEnabledPerm("action.chain#import.wsp")
	}

	async execute(ctx: IRegPointer<IWspUiEnv>, ev?: Event): Promise<Wsp | null> {
		const reg = ctx.reg;
		const {WspCreateFromScwspProps} = await import("/@back@/wsp/dialogs/wspProps.js");
		const wsp = await POPUP.showDialog<Wsp | null>(new WspCreateFromScwspProps().initialize({
			reg: ctx.reg,
		}), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: "Import d'un atelier à partir d'une archive"}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			fixSize: false
		}).onNextClose();
		this.openWspAfterCreate(ctx, wsp);
		return wsp;
	}
}

/** Gestion d'un atelier */
export class ManageWspsAction extends ChainAction<IRegPointer<IChainEnv>> {

	constructor(id?: string) {
		super(id || "manageWsps");
		this.checkCompatKey = "formElt";
		this._label = "Gérer les ateliers...";
		this._group = "manage";
		this.requireEnabledPerm("action.chain#manage.wsps");
	}

	async execute(ctx: IRegPointer<IWspUiEnv>, ev?: Event): Promise<void> {
		const {WspsMgr} = await import("/@back@/wsp/dialogs/wspsMgr.js");
		await POPUP.showDialog(new WspsMgr().initialize({
			reg: ctx.reg,
		}), ctx.reg.env.uiRoot, {
			closeOnCtrlEnter: true,
			titleBar: {barLabel: {label: "Gestion des ateliers"}, closeButton: {}},
			resizer: {},
			initWidth: "min(90vw,60em)",
			initHeight: "90vh"
		}).onNextClose();
	}
}